import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Box } from '@mui/system';
import dayjs from 'dayjs';
import { BasicButton, COLORS, ToggleButtonGroup, MultiSelectControlledOption } from 'components';
import { EsgMultiselectControlledField } from 'components/inputs/esg-multi-select-field';
import { useUtilityBillsYears } from 'hooks';

interface ScorecardFiltersProps {
    onFiltersChange?: (filters: {
        selectedFiscalYears: MultiSelectControlledOption[];
        selectedToggleOption: 'INPUT_UNIT' | 'DEFAULT_UNIT';
    }) => void;
    disabledToggle?: boolean;
}

export const ScorecardFilters = ({ onFiltersChange, disabledToggle = false }: ScorecardFiltersProps) => {
    const { data: availableYears } = useUtilityBillsYears();
    const [selectedFiscalYears, setSelectedFiscalYears] = useState<MultiSelectControlledOption[]>([]);
    const [selectedToggleOption, setSelectedToggleOption] = useState<'INPUT_UNIT' | 'DEFAULT_UNIT'>('INPUT_UNIT');

    const allFiscalYears: MultiSelectControlledOption[] = useMemo(
        () =>
            (availableYears &&
                availableYears.map((fy: number) => ({
                    label: `${fy} Fiscal Year`,
                    value: `${fy} Fiscal Year`,
                    name: `${fy} Fiscal Year`
                }))) || [
                {
                    label: `${dayjs().get("year")} Fiscal Year`,
                    value: `${dayjs().get("year")} Fiscal Year`,
                    name: `${dayjs().get("year")} Fiscal Year`
                }
            ],
        [availableYears],
    );

    const notifyFiltersChange = useCallback((fiscalYears: MultiSelectControlledOption[], toggleOption: 'INPUT_UNIT' | 'DEFAULT_UNIT') => {
        if (onFiltersChange) {
            onFiltersChange({
                selectedFiscalYears: fiscalYears,
                selectedToggleOption: toggleOption,
            });
        }
    }, [onFiltersChange]);

    useEffect(() => {
        if (availableYears && availableYears.length > 0 && selectedFiscalYears.length === 0) {
            const currentYear = dayjs().get("year");
            const currentFiscalYear = availableYears.includes(currentYear)
                ? currentYear
                : availableYears[availableYears.length - 1]; // Use latest available year if current year not available

            const defaultFiscalYear = {
                label: `${currentFiscalYear} Fiscal Year`,
                value: `${currentFiscalYear} Fiscal Year`,
                name: `${currentFiscalYear} Fiscal Year`
            };

            setSelectedFiscalYears([defaultFiscalYear]);
            notifyFiltersChange([defaultFiscalYear], selectedToggleOption);
        }
    }, [availableYears, selectedFiscalYears.length, selectedToggleOption, notifyFiltersChange]);

    const onFiscalYearsChange = (fiscalYears: MultiSelectControlledOption[]) => {
        // Sort fiscal years chronologically by extracting the year number
        const sortedFiscalYears = [...fiscalYears].sort((a, b) => {
            const yearA = parseInt(a.value.toString().split(' ')[0], 10);
            const yearB = parseInt(b.value.toString().split(' ')[0], 10);
            return yearA - yearB;
        });
        setSelectedFiscalYears(sortedFiscalYears);
    }

    const onToggleOptionChange = (value: 'INPUT_UNIT' | 'DEFAULT_UNIT') => {
        setSelectedToggleOption(value);
        notifyFiltersChange(selectedFiscalYears, value);
    }

    const handleSearch = () => {
        notifyFiltersChange(selectedFiscalYears, selectedToggleOption);
    };

    const isSearchEnabled = useMemo(() => selectedFiscalYears.length > 0, [selectedFiscalYears]);

    const toggleOptions = [
        { value: 'DEFAULT_UNIT', label: 'Default Unit' },
        { value: 'INPUT_UNIT', label: 'Input Unit' }
    ];

    return <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <Box sx={{ width: "200px" }}>
            <EsgMultiselectControlledField
                label="Fiscal Years"
                selectedValues={selectedFiscalYears}
                setSelectedValues={onFiscalYearsChange}
                options={allFiscalYears}
                type="checkMissing"
                isAllSelectedOnEmptySelection={false}
            />
        </Box>
        <BasicButton
            variant="contained"
            sx={{ width: "120px", color: 'white', marginRight: '0px', borderColor: !isSearchEnabled ? 'gray' : COLORS.Romance }}
            onClick={handleSearch}
            disabled={!isSearchEnabled}
        >
            Search
        </BasicButton>
        <ToggleButtonGroup
            options={toggleOptions}
            selectedValue={selectedToggleOption}
            onValueChange={(value) => onToggleOptionChange(value as 'INPUT_UNIT' | 'DEFAULT_UNIT')}
            height="36px"
            sx={{ marginTop: '22px' }}
            disabled={disabledToggle}
        />
    </Box>;
};